// src/components/BusinessAreasLanding.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  ChevronRight,
  ChevronLeft,
  RotateCw,
  AlertTriangle,
  ArrowRight
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import useSessionState from "@/hooks/useSessionState";
import { OptimizedStarryBackground } from "./OptimizedStarryBackground";
import { getBusinessAreaConfig, BusinessAreaConfig, getRandomColorProfile } from "@/config/businessAreasConfig";

// Interface för SharePoint-objektet
interface SharePointItem {
  Delavbolag?: string | null;
}

interface BusinessAreasLandingProps {
  accessToken: string;
  onSelectArea: (areaName: string) => void;
}

const BusinessAreasLanding: React.FC<BusinessAreasLandingProps> = ({
  accessToken,
  onSelectArea,
}) => {
  const [areas, setAreas] = useState<BusinessAreaConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { toast } = useToast();
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const touchStartY = useRef(0);  // För att hantera y-position
  const touchEndY = useRef(0);    // För att hantera y-position
  const isTouchActive = useRef(false);
  
  // Lagra senast valda affärsområde i sessionStorage
  const [lastViewedArea, setLastViewedArea] = useSessionState<string | null>(
    "lastViewedArea",
    null
  );

  // Detektera mobilvy baserat på fönsterstorlek
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // När områdena har laddats, scrolla in den senast visade (om sparat)
  useEffect(() => {
    if (lastViewedArea) {
      const el = document.getElementById(`area-card-${lastViewedArea}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [lastViewedArea, areas]);

  // Hämta unika "Del av bolag" från SharePoint och generera "areas"
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        setLoading(true);
        setError(null);

        // Uppdaterad URL som pekar på den nya SharePoint-sidan
        const apiUrl =
          "https://solkompaniet365.sharepoint.com/sites/SolkompanietProcesser" +
          "/_api/web/lists/getbytitle('Process Viewer CMS')/items" +
          "?$select=Delavbolag" +
          "&$orderby=Delavbolag asc";

        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            Accept: "application/json;odata=nometadata",
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }

        const json = await response.json();
        if (!json.value) {
          throw new Error("No data found.");
        }

        const data = json.value as SharePointItem[];

        // Extrahera unika affärsområden från data
        const uniqueNames: string[] = [
          ...new Set(
            data
              .map((item) => item.Delavbolag?.trim() ?? "")
              .filter((s) => s.length > 0)
          ),
        ];

        // Om inga unika områden hittas, lägg till ett standard "Alla" område
        if (uniqueNames.length === 0) {
          uniqueNames.push("Alla");
        }

        // Skapa konfigurerade områden baserat på namnen från SharePoint
        const configuredAreas: BusinessAreaConfig[] = uniqueNames.map((name) => {
          // Hitta konfiguration för detta område, eller fallback
          const config = getBusinessAreaConfig(name);
          
          // Skapa en anpassad version med rätt namn
          return {
            ...config,
            name, // Använd det faktiska namnet från SharePoint
            fallback: false, // Detta är inte en fallback längre
          };
        });

        setAreas(configuredAreas);

        if (configuredAreas.length === 0) {
          setError("Inga affärsområden definierade i SharePoint.");
          toast({
            title: "Information",
            description: "Inga affärsområden hittades.",
            duration: 5000,
          });
        } else if (lastViewedArea) {
          const lastIndex = configuredAreas.findIndex(area => area.name === lastViewedArea);
          if (lastIndex >= 0) {
            setCurrentIndex(lastIndex);
          }
        }
      } catch (err) {
        console.error("API Error:", err);
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(errorMessage);
        toast({
          title: "Fel vid hämtning av affärsområden",
          description: errorMessage,
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) {
      fetchAreas();
    }
  }, [accessToken, toast, lastViewedArea]);

  // Hämta nästa eller föregående element i en cirkulär lista
  const getCircularIndex = (currentIdx: number, offset: number, length: number): number => {
    // Lägg till längden för att hantera negativa offset korrekt
    return (currentIdx + offset + length) % length;
  };

  // Hantera navigering i karusell
  const navigateCarousel = (dir: "next" | "prev") => {
    if (!areas.length || isTransitioning) return;
    
    setIsTransitioning(true);
    
    if (dir === "next") {
      setCurrentIndex(prev => (prev === areas.length - 1 ? 0 : prev + 1));
    } else {
      setCurrentIndex(prev => (prev === 0 ? areas.length - 1 : prev - 1));
    }
    
    setTimeout(() => setIsTransitioning(false), 500);
  };

  useEffect(() => {
    if (areas.length <= 1) return;
    
    const startAutoPlay = () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      autoPlayRef.current = setInterval(() => {
        navigateCarousel("next");
      }, 8000);
    };
    
    startAutoPlay();
    
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [currentIndex, areas.length]);

  // Förbättrad swipe-logik med bättre hantering av swipes
  const handleTouchStart = (e: React.TouchEvent) => {
    // Återställ touch-positionerna vid varje start
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchEndX.current = 0;
    touchEndY.current = 0;
    isTouchActive.current = true;
    
    // Pausa autoplay när användaren interagerar med karusellen
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isTouchActive.current) return;
    
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
  };
  
  const handleTouchEnd = () => {
    if (!isTouchActive.current) return;
    
    // Beräkna skillnad i både x och y riktning
    const diffX = touchStartX.current - touchEndX.current;
    const diffY = Math.abs(touchStartY.current - touchEndY.current);
    
    // Om vi inte har touch-end värden, avbryt
    if (touchEndX.current === 0) {
      isTouchActive.current = false;
      return;
    }
    
    // Kontrollera att den horisontella rörelsen är 
    // 1) tillräckligt stor och 
    // 2) inte primärt en vertikal scrollning
    const MIN_SWIPE_DISTANCE = 30;
    const VERTICAL_LIMIT_RATIO = 1.2; // Horisontellt > vertikalt * 1.2
    
    if (Math.abs(diffX) > MIN_SWIPE_DISTANCE && Math.abs(diffX) > diffY * VERTICAL_LIMIT_RATIO) {
      // Paus auto-play medan användaren interagerar
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      
      if (diffX > 0) {
        // Svep åt vänster -> nästa
        navigateCarousel("next");
      } else {
        // Svep åt höger -> föregående
        navigateCarousel("prev");
      }
    }
    
    // Återställ flaggan
    isTouchActive.current = false;
    
    // Starta om auto-play efter interaktion
    const startAutoPlayAfterInteraction = () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      autoPlayRef.current = setInterval(() => {
        navigateCarousel("next");
      }, 8000);
    };
    
    startAutoPlayAfterInteraction();
  };

  const handleAreaSelection = (areaName: string) => {
    setLastViewedArea(areaName);
    onSelectArea(areaName);
  };

  // Visuell indikator för swipe-möjlighet (enklare version för bättre mobilanpassning)
  const SwipeIndicator = () => (
    <div className="absolute bottom-20 left-0 right-0 flex justify-center items-center md:hidden">
      <motion.div 
        initial={{ opacity: 0.7 }}
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        className="bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1"
      >
        <motion.div 
          animate={{ x: [-3, 0, -3] }}
          transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
        >
          <ChevronLeft className="h-3 w-3 text-white/70" />
        </motion.div>
        <span className="text-xs text-white/80">Svep</span>
        <motion.div 
          animate={{ x: [3, 0, 3] }}
          transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
        >
          <ChevronRight className="h-3 w-3 text-white/70" />
        </motion.div>
      </motion.div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <OptimizedStarryBackground />
        <div className="flex flex-col items-center gap-4">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
            className="w-12 h-12 border-t-2 border-r-2 border-orange-300 rounded-full"
          />
          <p className="text-gray-300">Laddar affärsområden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 text-white">
        <OptimizedStarryBackground />
        <div className="bg-gray-800/70 p-6 rounded-xl border border-gray-700 max-w-md w-full">
          <div className="flex items-center justify-center text-red-400 mb-4">
            <AlertTriangle className="h-10 w-10" />
          </div>
          <h2 className="text-xl font-bold text-center mb-4">Det uppstod ett fel</h2>
          <p className="text-gray-300 text-center mb-6">{error}</p>
          <div className="flex justify-center">
            <Button 
              onClick={() => window.location.reload()}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <RotateCw className="h-4 w-4 mr-2" />
              Försök igen
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Om inga områden hittades, lägg till ett standard "Alla" område
  if (areas.length === 0) {
    const allConfig = getBusinessAreaConfig("Alla");
    setAreas([{
      ...allConfig,
      name: "Alla",
      fallback: false
    }]);
    return null; // Returnera null för att undvika rendering innan areas uppdaterats
  }

  if (areas.length === 1) {
    const single = areas[0];
    const { colorProfile } = single;
    
    return (
      <div className="min-h-screen relative text-white">
        <OptimizedStarryBackground />
        <div className="container mx-auto px-4 h-screen flex items-center justify-center">
          <motion.div 
            className="w-full max-w-xl" 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="text-center mb-8 md:mb-12">
              <motion.div 
                className="flex justify-center mb-6" 
                initial={{ y: -20, opacity: 0 }} 
                animate={{ y: 0, opacity: 1 }} 
                transition={{ delay: 0.3 }}
              >
                <img src="/solkompaniet_logo.png" alt="Solkompaniet" className="h-16 md:h-28" />
              </motion.div>
              <motion.h1 
                className="text-3xl md:text-5xl font-bold mb-4 md:mb-6 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600" 
                initial={{ scale: 0.9, opacity: 0 }} 
                animate={{ scale: 1, opacity: 1 }} 
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                Process Viewer
              </motion.h1>
              <motion.p 
                className="text-base md:text-lg text-gray-300" 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                transition={{ delay: 0.7 }}
              >
               Maxad ordning. Minimal ansträngning.
              </motion.p>
            </div>
            <motion.div 
              className="rounded-2xl overflow-hidden shadow-2xl hover:shadow-orange-500/10 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              whileHover={{ y: -5 }}
              onClick={() => handleAreaSelection(single.name)}
              id={`area-card-${single.name}`}
            >
              <div className={`${colorProfile.gradient} p-6 md:p-8`}>
                {/* Ikondelen */}
                <div className="p-4 bg-white/10 rounded-full mb-4 inline-block">
                  <single.icon className="h-8 w-8 md:h-12 md:w-12" />
                </div>
                <h2 className={`text-2xl md:text-3xl font-bold mb-3 ${colorProfile.text}`}>{single.name}</h2>
                <p className="text-white/90 mb-6 text-base md:text-lg">{single.description}</p>
                <div className="flex justify-center mt-6">
                  <motion.button 
                    className="px-6 py-3 md:px-8 md:py-4 bg-white text-gray-900 rounded-full font-medium flex items-center gap-2 hover:bg-white/90 transition-colors group shadow-xl"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Utforska 
                    <motion.div animate={{ x: [0, 4, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                      <ArrowRight className="h-5 w-5" />
                    </motion.div>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  // För flera områden, centrera karusellen
  return (
    <div 
      className="min-h-screen relative text-white flex items-center justify-center touch-none"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <OptimizedStarryBackground />
      <div className="container mx-auto px-4">
        <div className="relative flex flex-col h-screen">
          <motion.div 
            className="pt-6 md:pt-12 pb-4 md:pb-6 text-center" 
            initial={{ opacity: 0, y: -20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex justify-center mb-3 md:mb-4">
              <img src="/solkompaniet_logo.png" alt="Solkompaniet" className="h-12 md:h-16" />
            </div>
            <motion.h1 
              className="text-2xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600" 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              Process Viewer
            </motion.h1>
            <motion.p 
              className="text-gray-300 text-sm md:text-base mt-1 md:mt-2 mb-4 md:mb-8" 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              Välj bolagsområde nedan
            </motion.p>
          </motion.div>
          
          <div className="flex-1 flex justify-center overflow-hidden">
            <div 
              ref={carouselRef} 
              className="relative w-full h-[350px] md:h-[400px] max-w-[95%] mx-auto md:max-w-6xl mt-2 md:mt-8"
            >
              {/* Swipe Indicator */}
              {areas.length > 1 && <SwipeIndicator />}
            
              {/* Visa endast pilknappar på större skärmar (ej på mobil) */}
              {!isMobile && (
                <>
                  <motion.button 
                    initial={{ opacity: 0, x: -10 }} 
                    animate={{ opacity: 1, x: 0 }} 
                    transition={{ delay: 0.8 }} 
                    onClick={() => navigateCarousel("prev")} 
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/10 hover:bg-white/20 rounded-full p-3 text-white transition-all backdrop-blur-md border border-white/10 shadow-lg" 
                    aria-label="Previous"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </motion.button>
                  <motion.button 
                    initial={{ opacity: 0, x: 10 }} 
                    animate={{ opacity: 1, x: 0 }} 
                    transition={{ delay: 0.8 }} 
                    onClick={() => navigateCarousel("next")} 
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/10 hover:bg-white/20 rounded-full p-3 text-white transition-all backdrop-blur-md border border-white/10 shadow-lg" 
                    aria-label="Next"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </motion.button>
                </>
              )}
              
              <div className="flex justify-center items-center relative h-full perspective-[1500px]">
                <div className="flex justify-center items-center w-full h-full perspective-[2500px]">
                  {areas.map((area, index) => {
                    const isActive = index === currentIndex;
                    const isPrev = (currentIndex === 0 && index === areas.length - 1) || (index === currentIndex - 1);
                    const isNext = (currentIndex === areas.length - 1 && index === 0) || (index === currentIndex + 1);
                    // Beräkna förhållanden för när fler kort ska visas
                    const isSecondPrev = (currentIndex === 1 && index === areas.length - 1) || 
                                        (currentIndex === 0 && index === areas.length - 2) || 
                                        (index === currentIndex - 2);
                    
                    const isSecondNext = (currentIndex === areas.length - 2 && index === 0) || 
                                        (currentIndex === areas.length - 1 && index === 1) || 
                                        (index === currentIndex + 2);
                    
                    // För större skärmar kan vi visa ytterligare kort
                    const isThirdPrev = !isMobile && ((currentIndex === 2 && index === areas.length - 1) || 
                                      (currentIndex === 1 && index === areas.length - 2) ||
                                      (currentIndex === 0 && index === areas.length - 3) ||
                                      (index === currentIndex - 3));
                    
                    const isThirdNext = !isMobile && ((currentIndex === areas.length - 3 && index === 0) || 
                                      (currentIndex === areas.length - 2 && index === 1) ||
                                      (currentIndex === areas.length - 1 && index === 2) ||
                                      (index === currentIndex + 3));
                    
                    let opacity = 0;
                    let scale = 0.7;
                    let zIndex = 0;
                    let translateX = "0%";
                    let translateZ = "-600px";
                    let rotateY = "0deg";
                    let visibility: 'visible' | 'hidden' = "visible";
                    
                    if (isActive) {
                      opacity = 1;
                      scale = 1;
                      zIndex = 50;
                      translateX = "0%";
                      translateZ = "0";
                      rotateY = "0deg";
                      visibility = "visible";
                    } else if (isPrev) {
                      opacity = 0.6;
                      scale = 0.8;
                      zIndex = 40;
                      translateX = "-65%";
                      translateZ = "-200px";
                      rotateY = "20deg";
                      visibility = "visible";
                    } else if (isNext) {
                      opacity = 0.6;
                      scale = 0.8;
                      zIndex = 40;
                      translateX = "65%";
                      translateZ = "-200px";
                      rotateY = "-20deg";
                      visibility = "visible";
                    } else if (isSecondPrev) {
                      opacity = 0.4;
                      scale = 0.65;
                      zIndex = 30;
                      translateX = "-115%";
                      translateZ = "-350px";
                      rotateY = "35deg";
                      visibility = "visible";
                    } else if (isSecondNext) {
                      opacity = 0.4;
                      scale = 0.65;
                      zIndex = 30;
                      translateX = "115%";
                      translateZ = "-350px";
                      rotateY = "-35deg";
                      visibility = "visible";
                    } else if (isThirdPrev) {
                      opacity = 0.2;
                      scale = 0.5;
                      zIndex = 20;
                      translateX = "-150%";
                      translateZ = "-500px";
                      rotateY = "50deg";
                      visibility = "visible";
                    } else if (isThirdNext) {
                      opacity = 0.2;
                      scale = 0.5;
                      zIndex = 20;
                      translateX = "150%";
                      translateZ = "-500px";
                      rotateY = "-50deg";
                      visibility = "visible";
                    } else {
                      opacity = 0;
                      visibility = "hidden";
                    }
                    
                    // Hämta färgprofilen för detta område
                    const { colorProfile } = area;
                    
                    return (
                      <motion.div
                        key={area.name}
                        id={`area-card-${area.name}`}
                        className="absolute transform-gpu"
                        initial={{ opacity: 0 }}
                        animate={{ 
                          opacity, 
                          scale, 
                          x: translateX, 
                          z: translateZ, 
                          rotateY: rotateY,
                          visibility
                        }}
                        transition={{ 
                          type: "spring", 
                          stiffness: 300, 
                          damping: 30, 
                          mass: 1.5,
                          delay: index === currentIndex ? 0 : 0.05,
                        }}
                        style={{ transformStyle: "preserve-3d", zIndex }}
                        onClick={() => {
                          if (isActive) {
                            handleAreaSelection(area.name);
                          } else {
                            setCurrentIndex(index);
                          }
                        }}
                      >
                        <div 
                          className={`relative w-[260px] sm:w-[300px] h-[350px] sm:h-[380px] md:w-[340px] md:h-[400px] cursor-pointer rounded-3xl overflow-hidden shadow-2xl transition-all duration-300`} 
                          style={{
                            boxShadow: isActive 
                              ? "0 25px 50px -12px rgba(249, 115, 22, 0.25)" 
                              : "0 15px 25px -8px rgba(0, 0, 0, 0.6)",
                            borderRadius: "1.5rem",
                            outline: isActive ? "none" : "1px solid rgba(255, 255, 255, 0.1)",
                          }}
                        >
                          <div className={`${colorProfile.gradient} w-full h-full relative border-0`}>
                            <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-transparent to-black/40"></div>
                            <div className="relative h-full flex flex-col items-center justify-between p-6 md:p-8 text-center">
                              <div>
                                <h2 className={`text-2xl md:text-4xl font-bold mb-3 md:mb-4 mt-4 md:mt-6 ${colorProfile.text}`}>
                                  {area.name}
                                </h2>
                                {/* Ikon för området */}
                                <div className="p-3 md:p-4 bg-white/10 rounded-full mb-3 md:mb-4 inline-block">
                                  <area.icon className="h-8 w-8 md:h-12 md:w-12" />
                                </div>
                              </div>
                              <motion.p 
                                className="text-white text-base md:text-lg my-4 md:my-6 max-w-[90%] md:max-w-[80%]" 
                                whileHover={{ scale: 1.05 }}
                              >
                                {area.description}
                              </motion.p>
                              <div className="mt-auto mb-8">
                                {isActive && (
                                  <motion.button 
                                    initial={{ opacity: 0, y: 20 }} 
                                    animate={{ opacity: 1, y: 0 }} 
                                    transition={{ delay: 0.2 }} 
                                    whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.2)" }} 
                                    whileTap={{ scale: 0.95 }} 
                                    className="px-6 py-3 md:px-10 md:py-4 bg-white text-gray-900 rounded-full font-medium flex items-center gap-2 hover:bg-white/90 transition-colors shadow-lg text-sm md:text-base" 
                                    onClick={(e) => { e.stopPropagation(); handleAreaSelection(area.name); }}
                                  >
                                    Utforska 
                                    <motion.div animate={{ x: [0, 4, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                                      <ArrowRight className="h-4 w-4 md:h-5 md:w-5" />
                                    </motion.div>
                                  </motion.button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <motion.div 
            className="absolute bottom-8 md:bottom-12 left-0 right-0 flex justify-center gap-1.5 md:gap-2" 
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.9 }}
          >
            {areas.map((_, index) => (
              <button 
                key={index} 
                onClick={() => setCurrentIndex(index)} 
                className={`transition-all duration-300 ${
                  index === currentIndex 
                    ? "w-8 md:w-10 h-1.5 md:h-2 bg-orange-500" 
                    : "w-1.5 md:w-2 h-1.5 md:h-2 bg-gray-600 hover:bg-gray-500"
                } rounded-full`} 
                aria-label={`Go to slide ${index + 1}`} 
              />
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BusinessAreasLanding;
